import { useEffect, useMemo, useState } from "react";
import { api, extractError } from "@/lib/api";
import { useBrands } from "@/contexts/BrandContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { UploadCloud, FileSpreadsheet, CheckCircle2, AlertCircle, Loader2, Sparkles, Calendar, Save, X } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { MARKETPLACE_OPTIONS, COUNTRY_OPTIONS, FIELD_LABELS } from "@/lib/marketplaces";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { DatePicker } from "@/components/app/DatePicker";
import { InfoTip } from "@/components/app/InfoTip";

function fmtBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export default function Upload() {
  const { selectedBrandId, selectedBrand } = useBrands();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [marketplace, setMarketplace] = useState(selectedBrand?.marketplace || "Amazon");
  const [country, setCountry] = useState(selectedBrand?.country || "India");
  const [weekStart, setWeekStart] = useState("");
  const [weekEnd, setWeekEnd] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [originalMapping, setOriginalMapping] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  // Phase 19.1 — async ingest. After the upload bytes finish transferring,
  // the server returns `{status:"Queued", upload_id}` immediately and the
  // heavy work happens in a background task. We then poll for live progress.
  const [serverProgress, setServerProgress] = useState(null); // {status, pct, processed_rows, total_rows, skipped_rows, error_log}
  const [error, setError] = useState("");
  const [tplDialog, setTplDialog] = useState(false);
  const [tplName, setTplName] = useState("");
  const [tplSaving, setTplSaving] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewProgress, setPreviewProgress] = useState(0);   // 0-100 upload bytes for preview
  const [previewServerWait, setPreviewServerWait] = useState(false); // true after upload finishes, before response
  const [processProgress, setProcessProgress] = useState(0);   // 0-100 upload bytes for process
  const [processServerWait, setProcessServerWait] = useState(false); // true after upload finishes, before response
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    if (selectedBrand?.marketplace) setMarketplace(selectedBrand.marketplace);
    if (selectedBrand?.country) setCountry(selectedBrand.country);
  }, [selectedBrand]);

  const onPickFile = async (f) => {
    setError("");
    setFile(f);
    setPreview(null);
    if (!f) return;
    setPreviewLoading(true);
    setPreviewProgress(0);
    setPreviewServerWait(false);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("marketplace", marketplace);
      fd.append("country", country);
      const { data } = await api.post("/uploads/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (!e.total) return;
          const pct = Math.min(100, Math.round((e.loaded / e.total) * 100));
          setPreviewProgress(pct);
          if (pct >= 100) setPreviewServerWait(true);
        },
      });
      setPreview(data);
      setMapping(data.auto_mapping || {});
      setOriginalMapping(data.auto_mapping || {});
      if (data.detected_week_start && !weekStart) setWeekStart(data.detected_week_start);
      if (data.detected_week_end && !weekEnd) setWeekEnd(data.detected_week_end);
      if (data.template_name) toast.success(`Template applied: ${data.template_name}`);
      setStep(3);
    } catch (e) {
      setError(extractError(e, "Could not parse CSV"));
    } finally {
      setPreviewLoading(false);
      setPreviewServerWait(false);
    }
  };

  const clearFile = () => {
    setFile(null); setPreview(null); setError("");
    setPreviewProgress(0); setPreviewLoading(false); setPreviewServerWait(false);
    const el = document.getElementById("csv-input");
    if (el) el.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) onPickFile(f);
  };

  const submit = async () => {
    if (!mapping.search_term) { toast.error("Search Term column not mapped. Please map before processing."); return; }
    if (!weekStart || !weekEnd) { toast.error("Please select a week range."); return; }
    setSubmitting(true); setError("");
    setProcessProgress(0); setProcessServerWait(false);
    setServerProgress(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("brand_id", selectedBrandId);
      fd.append("marketplace", marketplace);
      fd.append("country", country);
      fd.append("week_start_date", weekStart);
      fd.append("week_end_date", weekEnd);
      fd.append("mapping_json", JSON.stringify(mapping));
      const { data } = await api.post("/uploads/process", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (!e.total) return;
          const pct = Math.min(100, Math.round((e.loaded / e.total) * 100));
          setProcessProgress(pct);
          if (pct >= 100) setProcessServerWait(true);
        },
      });
      // Async ingest: server returned {upload_id, status:"Queued"}. Poll
      // /status every 2s until we hit Processed or Failed (or 30 min wall).
      const uploadId = data.upload_id;
      setServerProgress({ status: "Queued", pct: 0, processed_rows: 0,
                          total_rows: data.total_rows || 0, skipped_rows: 0 });
      const start = Date.now();
      let finalStatus = null;
      while (Date.now() - start < 30 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const sr = await api.get(`/uploads/${uploadId}/status`);
          setServerProgress(sr.data);
          if (sr.data.status === "Processed" || sr.data.status === "Failed") {
            finalStatus = sr.data;
            break;
          }
        } catch (pollErr) {
          // Polling errors are non-fatal — keep trying.
        }
      }
      if (!finalStatus) throw new Error("Processing exceeded 30 min wall clock");
      if (finalStatus.status === "Failed") {
        throw new Error(finalStatus.error_log || "Upload failed during processing");
      }
      setResult({ ...data, ...finalStatus });
      setStep(5);
      if (data.merged) {
        toast.success(`Merged into existing week — ${finalStatus.processed_rows} rows`);
      } else {
        toast.success(`Processed ${finalStatus.processed_rows} rows`);
      }
    } catch (e) {
      const msg = extractError(e, e.message || "Upload failed");
      setError(msg); toast.error(msg);
    } finally { setSubmitting(false); setProcessServerWait(false); }
  };

  const mappingChanged = useMemo(() => {
    const a = JSON.stringify(mapping || {});
    const b = JSON.stringify(originalMapping || {});
    return a !== b;
  }, [mapping, originalMapping]);

  const saveAsTemplate = async () => {
    if (!tplName.trim()) return toast.error("Template name required");
    setTplSaving(true);
    try {
      if (preview?.template_id) {
        // update existing
        await api.put(`/marketplace-templates/${preview.template_id}`, {
          template_name: tplName,
          marketplace, country,
          column_mapping: mapping,
          is_default: true,
          status: "active",
          notes: `Updated from upload of ${file?.name || ""}`,
        });
        toast.success("Template updated");
      } else {
        await api.post("/marketplace-templates", {
          template_name: tplName,
          marketplace, country,
          column_mapping: mapping,
          is_default: true,
          status: "active",
          notes: `Captured from upload of ${file?.name || ""}`,
        });
        toast.success("Template saved");
      }
      setOriginalMapping({ ...mapping });
      setTplDialog(false);
      setTplName("");
    } catch (e) { toast.error(extractError(e)); }
    finally { setTplSaving(false); }
  };

  const isAdmin = user?.role === "admin";

  const Step = ({ n, label }) => (
    <div className="flex items-center gap-2">
      <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold ${
        step >= n ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-500"}`}>{n}</div>
      <span className={`text-sm font-semibold ${step >= n ? "text-slate-900" : "text-slate-500"}`}>{label}</span>
    </div>
  );

  if (!selectedBrandId) return <div className="text-sm text-slate-500">Select a brand from the top bar.</div>;

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="upload-wizard">
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Execution</p>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Weekly CSV upload</h1>
        <p className="mt-1 text-sm text-slate-500">
          Upload Top Search Terms for <span className="font-semibold text-slate-900">{selectedBrand?.brand_name}</span>.
          The system auto-applies the marketplace's column-mapping template.
        </p>
      </div>

      <div className="flex items-center gap-6 border-b border-slate-200 pb-4">
        <Step n={1} label="Setup" /><div className="h-px flex-1 bg-slate-200" />
        <Step n={2} label="File" /><div className="h-px flex-1 bg-slate-200" />
        <Step n={3} label="Mapping" /><div className="h-px flex-1 bg-slate-200" />
        <Step n={4} label="Process" /><div className="h-px flex-1 bg-slate-200" />
        <Step n={5} label="Done" />
      </div>

      {step === 1 && (
        <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm space-y-5">
          <h3 className="text-lg font-semibold text-slate-900 tracking-tight">Reporting setup</h3>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Marketplace">
              <Select value={marketplace} onValueChange={setMarketplace}>
                <SelectTrigger data-testid="upload-marketplace"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MARKETPLACE_OPTIONS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Country">
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger data-testid="upload-country"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COUNTRY_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-xs text-blue-800 flex items-start gap-2">
            <Sparkles className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>Week range will be auto-detected from your CSV. You can adjust it after preview.</span>
          </div>
          <div className="flex justify-end pt-2">
            <Button data-testid="upload-step1-next" onClick={() => setStep(2)} className="bg-slate-900 text-white hover:bg-slate-800">Continue</Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900 tracking-tight mb-4">
            Upload {marketplace} {marketplace === "Amazon" ? "Top Search Terms" : "search data"} CSV
          </h3>
          {!file && (
            <label
              htmlFor="csv-input"
              data-testid="upload-dropzone"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              className={`block border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${
                dragOver ? "border-slate-900 bg-slate-100" : "border-slate-300 bg-slate-50 hover:bg-slate-100"
              }`}
            >
              <UploadCloud strokeWidth={1.3} className="h-10 w-10 mx-auto text-slate-400" />
              <p className="mt-3 text-sm font-semibold text-slate-700">{dragOver ? "Drop the file here" : "Click to choose CSV or drag here"}</p>
              <p className="mt-1 text-xs text-slate-500">.csv up to 100,000 rows · we auto-apply the saved template for {marketplace} {country}</p>
              <input id="csv-input" type="file" accept=".csv" className="hidden" onChange={(e) => onPickFile(e.target.files?.[0])} />
            </label>
          )}

          {file && (
            <div data-testid="upload-file-card" className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="h-11 w-11 rounded-lg bg-slate-900 text-white flex items-center justify-center shrink-0">
                  <FileSpreadsheet strokeWidth={1.5} className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div data-testid="upload-file-name" className="font-semibold text-slate-900 truncate">{file.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        <span data-testid="upload-file-size">{fmtBytes(file.size)}</span>
                        <span className="mx-1.5 text-slate-300">·</span>
                        <span>{file.type || "text/csv"}</span>
                      </div>
                    </div>
                    {!previewLoading && (
                      <button
                        data-testid="upload-clear-file"
                        onClick={clearFile}
                        className="rounded-md p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                        title="Choose a different file"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  {previewLoading && (
                    <div className="mt-3" data-testid="upload-preview-progress">
                      <Progress value={previewProgress} className="h-2" />
                      <div className="mt-2 flex items-center justify-between text-[11px] font-medium">
                        {previewServerWait ? (
                          <span className="inline-flex items-center gap-1.5 text-amber-700">
                            <Loader2 className="h-3 w-3 animate-spin" /> Server is parsing the CSV…
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-slate-700">
                            <Loader2 className="h-3 w-3 animate-spin" /> Uploading… {previewProgress}%
                          </span>
                        )}
                        <span className="text-slate-400">{previewServerWait ? "almost there" : `${previewProgress}/100`}</span>
                      </div>
                    </div>
                  )}

                  {!previewLoading && preview && (
                    <div className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-800">
                      <CheckCircle2 className="h-3 w-3" /> Parsed · {preview.total_rows.toLocaleString()} rows · {preview.headers.length} columns
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {error && <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">{error}</div>}
          <div className="flex justify-between mt-6">
            <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
            {file && !previewLoading && preview && (
              <Button data-testid="upload-step2-next" onClick={() => setStep(3)} className="bg-slate-900 text-white hover:bg-slate-800">Continue to mapping</Button>
            )}
          </div>
        </div>
      )}

      {step === 3 && preview && (
        <div className="space-y-5">
          {preview.template_name ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 flex items-center gap-2" data-testid="upload-template-banner">
              <CheckCircle2 className="h-4 w-4" />
              <span>Auto-applied template: <span className="font-semibold">{preview.template_name}</span></span>
            </div>
          ) : (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5" />
              <span>No saved template for this marketplace — using best-effort column detection. Save one in Masters → Marketplace Templates after this upload.</span>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4">
            <h3 className="text-base font-semibold text-slate-900 tracking-tight flex items-center gap-2">
              <Calendar strokeWidth={1.5} className="h-4 w-4" /> Reporting week
            </h3>
            {preview.detected_week_start ? (
              <p className="text-xs text-slate-500">Auto-detected from CSV: <span className="font-semibold text-slate-700">{preview.detected_week_start} → {preview.detected_week_end}</span>. You can adjust.</p>
            ) : (
              <p className="text-xs text-slate-500">No week metadata found in CSV — please pick the reporting week.</p>
            )}
            <div className="grid grid-cols-2 gap-4">
              <Field label="Week start"><DatePicker testid="upload-week-start" value={weekStart} onChange={setWeekStart} placeholder="Select start" /></Field>
              <Field label="Week end"><DatePicker testid="upload-week-end" value={weekEnd} onChange={setWeekEnd} placeholder="Select end" /></Field>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-900 tracking-tight">Map columns</h3>
              <span className="text-xs text-slate-500">{preview.total_rows.toLocaleString()} rows · {preview.headers.length} columns</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {FIELD_LABELS.map(([key, label, req]) => (
                <Field key={key} label={req ? `${label} *` : label}>
                  <Select value={mapping[key] || "__none__"} onValueChange={(v) => setMapping({ ...mapping, [key]: v === "__none__" ? undefined : v })}>
                    <SelectTrigger data-testid={`map-${key}`}><SelectValue placeholder="Not mapped" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Not mapped —</SelectItem>
                      {preview.headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              ))}
            </div>

            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                <FileSpreadsheet strokeWidth={1.5} className="h-3.5 w-3.5" /> First 5 rows preview
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr>{preview.headers.map((h) => <th key={h} className="px-3 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {preview.sample_rows.slice(0, 5).map((row, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        {preview.headers.map((h) => <td key={h} className="px-3 py-1.5 text-slate-700 whitespace-nowrap">{String(row[h] ?? "").slice(0, 80)}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {error && <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">{error}</div>}

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
              <div className="flex items-center gap-2">
                {isAdmin && mappingChanged && (
                  <Dialog open={tplDialog} onOpenChange={setTplDialog}>
                    <DialogTrigger asChild>
                      <Button data-testid="upload-save-template" variant="outline" className="gap-2" onClick={() => setTplName(preview?.template_name || `${marketplace} ${country} — Top Search Terms`)}>
                        <Save className="h-3.5 w-3.5" /> Save mapping as template
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{preview?.template_id ? "Update existing template" : "Save as new template"}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-3 py-2">
                        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Template name</Label>
                        <Input data-testid="upload-tpl-name" value={tplName} onChange={(e) => setTplName(e.target.value)} />
                        <p className="text-xs text-slate-500">
                          Marketplace: <b>{marketplace}</b> · Country: <b>{country}</b> · {Object.keys(mapping).length} fields mapped.
                          This will become the <b>default</b> template for this marketplace.
                        </p>
                      </div>
                      <DialogFooter>
                        <Button data-testid="upload-tpl-confirm" onClick={saveAsTemplate} disabled={tplSaving} className="bg-slate-900 text-white hover:bg-slate-800">
                          {tplSaving ? "Saving…" : (preview?.template_id ? "Update template" : "Save template")}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
                <Button data-testid="upload-process-btn" onClick={() => { setStep(4); submit(); }} className="bg-slate-900 text-white hover:bg-slate-800">
                  Process file
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="bg-white border border-slate-200 rounded-xl p-10 shadow-sm" data-testid="upload-process-step">
          {submitting ? (
            <div className="space-y-5 max-w-xl mx-auto">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-slate-900 text-white flex items-center justify-center shrink-0">
                  <FileSpreadsheet className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{file?.name}</div>
                  <div className="text-xs text-slate-500">{fmtBytes(file?.size)}</div>
                </div>
              </div>

              <div data-testid="upload-process-progress">
                <Progress value={serverProgress?.pct ?? processProgress} className="h-2.5" />
                <div className="mt-2 flex items-center justify-between text-xs font-medium">
                  {serverProgress ? (
                    <span data-testid="upload-server-progress" className="inline-flex items-center gap-1.5 text-amber-700">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Processing {serverProgress.processed_rows?.toLocaleString() || 0}
                      {" / "}
                      {serverProgress.total_rows?.toLocaleString() || 0} rows
                      {serverProgress.skipped_rows ? ` · ${serverProgress.skipped_rows.toLocaleString()} skipped` : ""}
                    </span>
                  ) : processServerWait ? (
                    <span className="inline-flex items-center gap-1.5 text-amber-700">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Queuing for background processing…
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-slate-700">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading file… {processProgress}%
                    </span>
                  )}
                  <span className="text-slate-400">
                    {serverProgress ? `${serverProgress.pct?.toFixed(1)}%` : `${processProgress}/100`}
                  </span>
                </div>
                {serverProgress?.inline_repo_sync === false && (
                  <div className="mt-1 text-[10px] text-slate-400 inline-flex items-center gap-1">
                    Large file detected — shared keyword repository sync deferred (run from Repository page).
                    <InfoTip term="REPO_DEFERRED" />
                  </div>
                )}
              </div>

              <ol className="text-xs text-slate-500 space-y-1.5 ml-1 mt-3">
                <li className="flex items-center gap-2">
                  <span className={processProgress >= 100 ? "text-emerald-600" : "text-slate-400"}>●</span> Transferring file to server
                </li>
                <li className="flex items-center gap-2">
                  <span className={(serverProgress && serverProgress.status === "Processing") ? "text-amber-600 animate-pulse" : (serverProgress?.status === "Processed" ? "text-emerald-600" : "text-slate-300")}>●</span>
                  Streaming rows in chunks, scoring opportunity & relevance
                  <InfoTip term="ASYNC_INGEST" />
                </li>
                <li className="flex items-center gap-2">
                  <span className={serverProgress?.status === "Processed" ? "text-emerald-600" : "text-slate-300"}>●</span>
                  Generating bidding & listing recommendations
                </li>
              </ol>
            </div>
          ) : error ? (
            <div className="text-center">
              <AlertCircle className="h-8 w-8 mx-auto text-rose-600" />
              <h3 className="mt-4 text-lg font-semibold text-slate-900">Processing failed</h3>
              <p className="mt-1 text-sm text-rose-600">{error}</p>
              <Button className="mt-5" variant="outline" onClick={() => setStep(3)}>Back to mapping</Button>
            </div>
          ) : null}
        </div>
      )}

      {step === 5 && result && (
        <div className="bg-white border border-slate-200 rounded-xl p-12 shadow-sm text-center">
          <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-600" />
          <h3 className="mt-4 text-2xl font-bold text-slate-900 tracking-tight">
            {result.merged ? "Merged into existing week" : "Upload complete"}
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            {result.merged ? (
              <>
                Added <span className="font-semibold text-slate-900">{(result.processed_rows || 0).toLocaleString()}</span> new terms ·
                updated <span className="font-semibold text-slate-900">{(result.updated_rows || 0).toLocaleString()}</span> ·
                skipped <span className="font-semibold text-slate-900">{(result.skipped_rows || 0).toLocaleString()}</span>.
              </>
            ) : (
              <>
                Processed <span className="font-semibold text-slate-900">{(result.processed_rows || 0).toLocaleString()}</span> rows ·
                skipped <span className="font-semibold text-slate-900">{(result.skipped_rows || 0).toLocaleString()}</span>.
              </>
            )}
          </p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <Button data-testid="upload-go-dashboard" onClick={() => navigate("/")} className="bg-slate-900 text-white hover:bg-slate-800">View dashboard</Button>
            <Button variant="outline" onClick={() => navigate("/search-terms")}>Open Search Terms</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
